import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { PublicClientApplication } from "@azure/msal-browser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = "'Segoe UI', Tahoma, sans-serif";

// Applique la session sur TOUS les appels sortants (axios + fetch) vers l'API.
function applySession(token) {
  axios.defaults.withCredentials = true;
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  if (!window.__fetchPatched) {
    window.__fetchPatched = true;
    const origFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url || "";
      if (url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
        const t = sessionStorage.getItem("app_token");
        init.credentials = init.credentials || "include";
        init.headers = { ...(init.headers || {}) };
        if (t && !init.headers["Authorization"]) init.headers["Authorization"] = `Bearer ${t}`;
      }
      return origFetch(input, init);
    };
  }
}

const winBox = {
  background: "#d4d0c8",
  border: "2px solid",
  borderColor: "#ffffff #808080 #808080 #ffffff",
  boxShadow: "1px 1px 0 #404040",
  padding: "24px 32px",
  minWidth: "420px",
};

const winBtn = {
  background: "#d4d0c8",
  border: "2px solid",
  borderColor: "#ffffff #808080 #808080 #ffffff",
  padding: "6px 18px",
  fontSize: "13px",
  cursor: "pointer",
  fontFamily: FONT,
};

function Screen({ children }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#fafafa", fontFamily: FONT,
    }}>
      {children}
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function LoginScreen({ mode, onLogin, error }) {
  const isMicrosoft = mode === "microsoft";
  return (
    <div style={{ minHeight: "100vh", background: "#fff", display: "flex", justifyContent: "center", fontFamily: FONT }}
         data-testid="login-screen">
      <div style={{ width: 460, maxWidth: "90%", paddingTop: 70 }}>
        {/* Logo Aide juridique du Québec */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <img src="/logo_ajq.png" alt="Aide juridique du Québec"
               style={{ width: 62, height: 62, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 27, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.12, letterSpacing: "-0.5px" }}>
              Aide juridique<br />du Québec
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: "#3b4a9f", marginTop: 3 }}>
              Commission des services juridiques
            </div>
          </div>
        </div>

        <div style={{ marginTop: 62 }}>
          <div style={{
            display: "inline-block", borderTop: "2px solid #1a1a1a", paddingTop: 7,
            fontSize: 12, letterSpacing: "3px", fontWeight: 600, color: "#1a1a1a",
          }}>
            AUTHENTIFICATION
          </div>
          <h1 style={{ fontSize: 38, fontWeight: 700, margin: "10px 0 8px", color: "#1a1a1a", letterSpacing: "-0.5px" }}
              data-testid="login-title">
            Admissibilité
            {process.env.REACT_APP_ENV_LABEL && (
              <span data-testid="env-badge" style={{
                display: "inline-block", verticalAlign: "middle", marginLeft: 12,
                padding: "3px 12px", fontSize: 14, fontWeight: 700, letterSpacing: "2px",
                color: "#fff", background: "#d97706", borderRadius: 6,
              }}>
                {process.env.REACT_APP_ENV_LABEL}
              </span>
            )}
          </h1>
          <p style={{ fontSize: 15, color: "#4a4a4a", margin: "0 0 28px" }}>
            Connectez-vous pour accéder à l'admissibilité à l'aide juridique.
          </p>

          <button
            onClick={onLogin}
            data-testid={isMicrosoft ? "btn-login-microsoft" : "btn-login-local"}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
              gap: 10, padding: "13px 20px", fontSize: 15, fontWeight: 500, color: "#1a1a1a",
              background: "#fff", border: "1px solid #d9d9d9", borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)", cursor: "pointer", fontFamily: FONT,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f7f7f7"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
          >
            {isMicrosoft ? <MicrosoftLogo /> : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b4a9f" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            )}
            {isMicrosoft ? "Se connecter avec Microsoft" : "Se connecter (profil local)"}
          </button>

          <p style={{ fontSize: 12.5, color: "#8a8a8a", textAlign: "center", marginTop: 16, lineHeight: 1.5 }}>
            {isMicrosoft
              ? <>La connexion par mot de passe est désactivée sur cet environnement.<br />Veuillez utiliser votre compte Microsoft.</>
              : <>Environnement de développement — la connexion Microsoft est désactivée.<br />Un profil local est utilisé avec les mêmes règles de gestion.</>}
          </p>

          {error && (
            <p style={{ color: "#b00000", fontSize: 12.5, textAlign: "center", marginTop: 10 }} data-testid="login-error">
              {error}
            </p>
          )}

          <p style={{ fontSize: 12.5, color: "#8a8a8a", textAlign: "center", marginTop: 44 }}>
            © 2026 Admissibilité — Édition Web
          </p>
        </div>
      </div>
    </div>
  );
}

function RegionSetup({ user, onDone }) {
  const [regions, setRegions] = useState([]);
  const [bureaux, setBureaux] = useState([]);
  const [region, setRegion] = useState("");
  const [bureau, setBureau] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/reference/regions`, { withCredentials: true })
      .then(r => setRegions(r.data || []))
      .catch(() => setError("Impossible de charger la liste des régions"));
  }, []);

  useEffect(() => {
    if (!region) { setBureaux([]); setBureau(""); return; }
    axios.get(`${API}/reference/bureaux-by-region`, { params: { region }, withCredentials: true })
      .then(r => {
        const list = r.data || [];
        setBureaux(list);
        setBureau(list.length === 1 ? list[0].code : "");
      })
      .catch(() => setBureaux([]));
  }, [region]);

  const save = async () => {
    if (!region) { setError("Veuillez sélectionner votre région de travail."); return; }
    if (!bureau) { setError("Veuillez sélectionner votre bureau de travail."); return; }
    setSaving(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/complete-profile`,
        { region, bureau }, { withCredentials: true });
      onDone(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'enregistrement du profil");
      setSaving(false);
    }
  };

  return (
    <Screen>
      <div style={winBox} data-testid="region-setup-screen">
        <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "6px" }}>
          Bienvenue{user?.nom_affichage ? `, ${user.nom_affichage}` : ""} !
        </div>
        <div style={{ fontSize: "13px", marginBottom: "16px" }}>
          C'est votre première connexion. Veuillez indiquer dans quelle région et quel
          bureau vous travaillez. Ils seront utilisés par défaut dans votre profil.
        </div>
        <div style={{ marginBottom: "14px" }}>
          <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>
            Région de travail *
          </label>
          <select
            data-testid="region-setup-select"
            value={region}
            onChange={e => setRegion(e.target.value)}
            style={{ width: "100%", padding: "5px", fontSize: "13px", border: "2px inset #d4d0c8", background: "#fff" }}
          >
            <option value="">-- Sélectionnez votre région --</option>
            {regions.map(r => (
              <option key={r.code} value={r.code}>
                {r.code}-{(r.Description || r.description || "").toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        {region && (
          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "bold", marginBottom: "4px" }}>
              Bureau de travail *
            </label>
            <select
              data-testid="region-setup-bureau-select"
              value={bureau}
              onChange={e => setBureau(e.target.value)}
              style={{ width: "100%", padding: "5px", fontSize: "13px", border: "2px inset #d4d0c8", background: "#fff" }}
            >
              <option value="">-- Sélectionnez votre bureau --</option>
              {bureaux.map(b => (
                <option key={b.code} value={b.code}>
                  {b.code} - {b.bureau || b.Description || b.description || ""}
                </option>
              ))}
            </select>
          </div>
        )}
        {error && <div style={{ color: "#b00000", fontSize: "12px", marginBottom: "10px" }} data-testid="region-setup-error">{error}</div>}
        <div style={{ textAlign: "right" }}>
          <button style={winBtn} onClick={save} disabled={saving} data-testid="region-setup-save-btn">
            {saving ? "Enregistrement..." : "Enregistrer mon profil"}
          </button>
        </div>
      </div>
    </Screen>
  );
}

export default function AuthGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const msalRef = useRef(null);
  const cfgRef = useRef(null);
  const initRan = useRef(false);

  const scopesFor = (cfg) => [`api://${cfg.api_client_id}/access_as_user`];

  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    (async () => {
      try {
        const cfg = (await axios.get(`${API}/auth/config`)).data;
        setConfig(cfg); cfgRef.current = cfg;
        if (!cfg.auth_enabled) {
          // Profil local : mêmes règles de gestion, écran de connexion identique.
          if (sessionStorage.getItem("local_session") === "1") {
            const me = await axios.get(`${API}/auth/me`);
            setUser(me.data);
          }
          setLoading(false);
          return;
        }
        const pca = new PublicClientApplication({
          auth: {
            clientId: cfg.spa_client_id,
            authority: `https://login.microsoftonline.com/${cfg.tenant_id}`,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
          },
          cache: { cacheLocation: "sessionStorage" },
        });
        await pca.initialize();
        msalRef.current = pca;
        // Jeton de session applicatif (en-tête Authorization — insensible au blocage des cookies tiers)
        const saved = sessionStorage.getItem("app_token");
        applySession(saved);
        const redirectResult = await pca.handleRedirectPromise();
        const account = redirectResult?.account || pca.getAllAccounts()[0];
        if (account) {
          const tok = await pca.acquireTokenSilent({ scopes: scopesFor(cfg), account });
          const ex = await axios.post(`${API}/auth/exchange`, {}, {
            headers: { Authorization: `Bearer ${tok.accessToken}` },
            withCredentials: true,
          });
          if (ex.data?.app_token) {
            sessionStorage.setItem("app_token", ex.data.app_token);
            applySession(ex.data.app_token);
          }
          const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(me.data);
        } else {
          try {
            // Pas de reconnexion auto si l'utilisateur vient de se déconnecter.
            if (sessionStorage.getItem("sso_logged_out") !== "1") {
              const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
              setUser(me.data);
            }
          } catch { /* pas de session — écran de connexion */ }
        }
      } catch (e) {
        console.error("AuthGate init error:", e);
        setError(e?.response?.data?.detail || e?.message || "Erreur d'initialisation de l'authentification");
      }
      setLoading(false);
    })();
  }, []);

  // Échange SSO → session applicative (utilisé après popup ou redirect)
  const completeSsoSession = useCallback(async (pca, cfg, account) => {
    const tok = await pca.acquireTokenSilent({ scopes: scopesFor(cfg), account });
    const ex = await axios.post(`${API}/auth/exchange`, {}, {
      headers: { Authorization: `Bearer ${tok.accessToken}` },
      withCredentials: true,
    });
    if (ex.data?.app_token) {
      sessionStorage.setItem("app_token", ex.data.app_token);
      applySession(ex.data.app_token);
    }
    const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(me.data);
  }, []);

  const loginMicrosoft = useCallback(async () => {
    sessionStorage.removeItem("sso_logged_out");
    const cfg = cfgRef.current;
    const pca = msalRef.current;
    if (!pca || !cfg) return;
    try {
      if (window.self !== window.top) {
        // Preview Emergent : l'app tourne dans un iframe et MSAL interdit
        // loginRedirect en iframe (redirect_in_iframe) → connexion par popup.
        const res = await pca.loginPopup({ scopes: scopesFor(cfg) });
        await completeSsoSession(pca, cfg, res.account);
      } else {
        pca.loginRedirect({ scopes: scopesFor(cfg) });
      }
    } catch (e) {
      console.error("loginMicrosoft:", e);
      setError(e?.response?.data?.detail || e?.message || "Échec de la connexion Microsoft");
    }
  }, [completeSsoSession]);

  const loginLocal = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/auth/me`);
      sessionStorage.setItem("local_session", "1");
      setUser(me.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de connexion au profil local");
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    sessionStorage.removeItem("local_session");
    sessionStorage.removeItem("app_token");
    sessionStorage.setItem("sso_logged_out", "1");
    delete axios.defaults.headers.common["Authorization"];
    if (msalRef.current) {
      // Déconnexion locale seulement (MSAL v5) : clearCache() vide les jetons et
      // le compte du navigateur SANS naviguer vers la déconnexion globale Microsoft.
      try { await msalRef.current.clearCache(); } catch { /* ignore */ }
    }
    window.location.reload();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#fff", display: "flex",
                    alignItems: "center", justifyContent: "center", fontFamily: FONT }}
           data-testid="auth-loading">
        <div style={{ fontSize: 14, color: "#8a8a8a" }}>Chargement de l'application...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <LoginScreen
        mode={config?.auth_enabled ? "microsoft" : "local"}
        onLogin={config?.auth_enabled ? loginMicrosoft : loginLocal}
        error={error}
      />
    );
  }

  if (user.needs_region) {
    return <RegionSetup user={user} onDone={setUser} />;
  }

  return children(user, logout);
}
